import { request, getToken, API_BASE } from '../base';
import type { ApiResponse, PaginatedResponse, PaginationParams } from '../base';

// Supplier Materials
export async function getSupplierMaterials(params?: { page?: number; pageSize?: number; supplierId?: string; rawMaterialId?: string; preferredOnly?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.supplierId) searchParams.set('supplierId', params.supplierId);
  if (params?.rawMaterialId) searchParams.set('rawMaterialId', params.rawMaterialId);
  if (params?.preferredOnly) searchParams.set('preferredOnly', 'true');
  return request<PaginatedResponse<any>>(`/supplier-materials?${searchParams.toString()}`);
}

export async function getMaterialSuppliers(materialId: string) {
  return request<{ data: any[] }>(`/supplier-materials/material/${materialId}/suppliers`);
}

export async function getSupplierMaterialsList(supplierId: string) {
  return request<{ data: any[] }>(`/supplier-materials/supplier/${supplierId}/materials`);
}

export async function createSupplierMaterial(data: {
  supplierId: string;
  rawMaterialId: string;
  supplierCode?: string;
  supplierName?: string;
  price: number;
  price2?: number;
  price3?: number;
  currency?: string;
  minOrderQty?: number;
  leadTimeDays?: number;
  validFrom?: string;
  validTo?: string;
  isPreferred?: boolean;
  notes?: string;
}) {
  return request<ApiResponse<{ id: string }>>('/supplier-materials', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSupplierMaterial(id: string, data: Partial<{
  supplierCode: string;
  supplierName: string;
  price: number;
  price2: number;
  price3: number;
  currency: string;
  minOrderQty: number;
  leadTimeDays: number;
  validFrom: string;
  validTo: string;
  isPreferred: boolean;
  notes: string;
}>) {
  return request<ApiResponse<{ id: string }>>(`/supplier-materials/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteSupplierMaterial(id: string) {
  return request<{ success: boolean }>(`/supplier-materials/${id}`, {
    method: 'DELETE',
  });
}

export async function setPreferredSupplier(materialId: string, supplierId: string) {
  return request<{ success: boolean }>(`/supplier-materials/material/${materialId}/set-preferred/${supplierId}`, {
    method: 'POST',
  });
}

// Alerts
export async function getLowStockAlerts() {
  return request<{ data: { items: any[]; summary: any } }>('/alerts/low-stock');
}

export async function getExpiringAlerts(days?: number) {
  const params = days ? `?days=${days}` : '';
  return request<{ data: { items: any[]; summary: any } }>(`/alerts/expiring${params}`);
}

export async function getPendingQcAlerts() {
  return request<{ data: any }>('/alerts/pending-qc');
}

export async function getProductionAlerts() {
  return request<{ data: any; summary: any }>('/alerts/production');
}

export async function getAlertsSummary() {
  return request<{ data: any; total: number }>('/alerts/summary');
}

// RFQs (Request for Quotes)
export async function getRfqs(params?: PaginationParams & { status?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  return request<PaginatedResponse<any>>(`/rfqs?${searchParams}`);
}

export async function getRfq(id: string) {
  return request<ApiResponse<any>>(`/rfqs/${id}`);
}

export async function createRfq(data: {
  title: string;
  dueDate: string;
  validUntil?: string;
  currency?: string;
  notes?: string;
  termsAndConditions?: string;
  lines: Array<{
    rawMaterialId: string;
    quantity: number;
    unit?: string;
    specifications?: string;
    targetPrice?: number;
  }>;
  supplierIds?: string[];
}) {
  return request<ApiResponse<{ id: string; rfqNumber: string }>>('/rfqs', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRfq(id: string, data: {
  title?: string;
  dueDate?: string;
  validUntil?: string;
  currency?: string;
  notes?: string;
  termsAndConditions?: string;
}) {
  return request<ApiResponse<{ id: string }>>(`/rfqs/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function sendRfq(id: string) {
  return request<{ success: boolean; message: string }>(`/rfqs/${id}/send`, {
    method: 'POST',
  });
}

export async function addSupplierToRfq(rfqId: string, supplierId: string) {
  return request<ApiResponse<{ id: string }>>(`/rfqs/${rfqId}/suppliers`, {
    method: 'POST',
    body: JSON.stringify({ supplierId }),
  });
}

export async function submitRfqResponse(rfqId: string, data: {
  supplierId: string;
  responses: Array<{
    rfqLineId: string;
    quotedPrice: number;
    currency?: string;
    leadTimeDays?: number;
    minOrderQuantity?: number;
    validUntil?: string;
    notes?: string;
  }>;
}) {
  return request<{ success: boolean }>(`/rfqs/${rfqId}/responses`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function awardRfq(rfqId: string, data: {
  supplierId: string;
  responseIds?: string[];
  createPurchaseOrder?: boolean;
}) {
  return request<{ success: boolean; data: { purchaseOrderId?: string; message: string } }>(`/rfqs/${rfqId}/award`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function cancelRfq(id: string) {
  return request<{ success: boolean }>(`/rfqs/${id}/cancel`, {
    method: 'POST',
  });
}

export async function deleteRfq(id: string) {
  return request<{ success: boolean }>(`/rfqs/${id}`, {
    method: 'DELETE',
  });
}

// Supplier Performance
export async function getSupplierPerformanceSummary(params?: { supplierId?: string; startDate?: string; endDate?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.supplierId) searchParams.set('supplierId', params.supplierId);
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  const query = searchParams.toString();
  return request<{ data: any[] }>(`/supplier-performance/summary${query ? `?${query}` : ''}`);
}

export async function getSupplierPerformanceHistory(supplierId: string, params?: { page?: number; pageSize?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  const query = searchParams.toString();
  return request<PaginatedResponse<any>>(`/supplier-performance/supplier/${supplierId}${query ? `?${query}` : ''}`);
}

export async function recordSupplierPerformance(data: {
  supplierId: string;
  purchaseOrderId?: string;
  metricType: 'delivery' | 'quality' | 'price' | 'responsiveness' | 'compliance';
  score: number;
  expectedDate?: string;
  actualDate?: string;
  notes?: string;
}) {
  return request<ApiResponse<{ id: string }>>('/supplier-performance', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function evaluateDeliveryPerformance(purchaseOrderId: string) {
  return request<{ data: { id: string; score: number; daysDiff: number; expectedDate: string; actualDate: string } }>(
    `/supplier-performance/evaluate-delivery/${purchaseOrderId}`,
    { method: 'POST' }
  );
}

export async function getSupplierRankings(params?: { metricType?: string; limit?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.metricType) searchParams.set('metricType', params.metricType);
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  const query = searchParams.toString();
  return request<{ data: any[] }>(`/supplier-performance/rankings${query ? `?${query}` : ''}`);
}

// Specifications & Certificates
export async function getFormulaSpecifications(formulaId: string) {
  return request<{ data: any }>(`/specifications/formula/${formulaId}`);
}

export async function getBatchCoa(batchId: string) {
  return request<{ data: any }>(`/specifications/coa/${batchId}`);
}

export async function getCoaReadyBatches() {
  return request<{ data: any[] }>('/specifications/coa-ready');
}

export async function getSpecificationTemplates() {
  return request<{ data: any[] }>('/specifications/templates');
}

export async function getCustomerSpecification(customerId: string, formulaId: string) {
  return request<{ data: any }>(`/specifications/customer/${customerId}/formula/${formulaId}`);
}

// Equipment
export async function getEquipmentCategories() {
  return request<{ data: any[] }>('/equipment/categories');
}

export async function getEquipment(params?: { category?: string; status?: string; search?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.category) searchParams.set('category', params.category);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.search) searchParams.set('search', params.search);
  const query = searchParams.toString();
  return request<{ data: any[] }>(`/equipment${query ? `?${query}` : ''}`);
}

export async function getEquipmentById(id: string) {
  return request<{ data: any }>(`/equipment/${id}`);
}

export async function createEquipment(data: any) {
  return request<{ data: any }>('/equipment', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateEquipment(id: string, data: any) {
  return request<{ data: any }>(`/equipment/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteEquipment(id: string) {
  return request<{ success: boolean }>(`/equipment/${id}`, { method: 'DELETE' });
}

export async function recordMaintenance(equipmentId: string, data: any) {
  return request<{ data: any }>(`/equipment/${equipmentId}/maintenance`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function recordCalibration(equipmentId: string, data: any) {
  return request<{ data: any }>(`/equipment/${equipmentId}/calibration`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getUpcomingMaintenance(days?: number) {
  const query = days ? `?days=${days}` : '';
  return request<{ data: any[]; summary: any }>(`/equipment/schedule/upcoming${query}`);
}

export async function getEquipmentStats() {
  return request<{ data: any }>('/equipment/stats/summary');
}

// Production Planning
export async function getPlanningCalendar(startDate?: string, endDate?: string) {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  const query = params.toString();
  return request<{ data: any[] }>(`/planning/calendar${query ? `?${query}` : ''}`);
}

export async function getPlanningCapacity(startDate?: string, endDate?: string) {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  const query = params.toString();
  return request<{ data: any[]; summary: any }>(`/planning/capacity${query ? `?${query}` : ''}`);
}

export async function getPlanningStats() {
  return request<{ data: any }>('/planning/stats');
}

export async function rescheduleProductionOrder(id: string, targetDate: string, notes?: string) {
  return request<{ success: boolean; message: string }>(`/planning/reschedule/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ targetDate, notes }),
  });
}

export async function getFormulasSummary() {
  return request<{ data: any[] }>('/planning/formulas-summary');
}

export async function getPlanningSuggestions(days?: number) {
  const query = days ? `?days=${days}` : '';
  return request<{ data: any[] }>(`/planning/suggestions${query}`);
}

// Demand Forecasting & Inventory Planning
export async function generateDemandForecasts(method: 'moving_average' | 'weighted_average' | 'trend_analysis' = 'moving_average', periodDays: number = 30) {
  return request<{ success: boolean; forecasts: any[]; summary: any }>('/planning/forecast/generate', {
    method: 'POST',
    body: JSON.stringify({ method, periodDays }),
  });
}

export async function getInventoryForecasts() {
  return request<{ forecasts: any[]; lastUpdated: string }>('/planning/forecast');
}

export async function generateReorderSuggestions() {
  return request<{ success: boolean; suggestions: any[]; summary: any }>('/planning/reorder/suggestions', {
    method: 'POST',
  });
}

export async function getReorderSuggestions() {
  return request<{ suggestions: any[]; lastUpdated: string }>('/planning/reorder/suggestions');
}

export async function getSafetyStockAnalysis() {
  return request<{ data: { analysis: any[]; summary: any } }>('/planning/safety-stock/analysis');
}

// Waste Tracking
export async function getWasteCategories() {
  return request<{ data: any[] }>('/waste/categories');
}

export async function getWasteRecords(params?: { startDate?: string; endDate?: string; category?: string; batchId?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  if (params?.category) searchParams.set('category', params.category);
  if (params?.batchId) searchParams.set('batchId', params.batchId);
  const query = searchParams.toString();
  return request<{ data: any[] }>(`/waste${query ? `?${query}` : ''}`);
}

export async function recordWaste(data: any) {
  return request<{ data: any }>('/waste', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateWasteRecord(id: string, data: any) {
  return request<{ data: any }>(`/waste/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteWasteRecord(id: string) {
  return request<{ success: boolean }>(`/waste/${id}`, { method: 'DELETE' });
}

export async function getWasteStats(startDate?: string, endDate?: string) {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  const query = params.toString();
  return request<{ data: any; period: any }>(`/waste/stats/summary${query ? `?${query}` : ''}`);
}

export async function getWastePareto(startDate?: string, endDate?: string) {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  const query = params.toString();
  return request<{ data: any[]; total: number }>(`/waste/stats/pareto${query ? `?${query}` : ''}`);
}

// Documents
export async function getDocumentTypes() {
  return request<{ data: any[] }>('/documents/types');
}

export async function getDocuments(params?: { entityType?: string; documentType?: string; search?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.entityType) searchParams.set('entityType', params.entityType);
  if (params?.documentType) searchParams.set('documentType', params.documentType);
  if (params?.search) searchParams.set('search', params.search);
  const query = searchParams.toString();
  return request<{ data: any[] }>(`/documents${query ? `?${query}` : ''}`);
}

export async function getDocumentsByEntity(entityType: string, entityId: string) {
  return request<{ data: any[] }>(`/documents/entity/${entityType}/${entityId}`);
}

export async function uploadDocument(file: File, entityType: string, entityId: string, documentType: string, description?: string) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('entityType', entityType);
  formData.append('entityId', entityId);
  formData.append('documentType', documentType);
  if (description) formData.append('description', description);

  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/documents/upload`, {
    method: 'POST',
    headers,
    body: formData,
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Upload failed' })) as { error?: string | { message?: string } };
    // Handle both { error: 'message' } and { error: { code, message } } formats
    const errorMessage = typeof errorData.error === 'object'
      ? errorData.error?.message || 'Upload failed'
      : errorData.error || `HTTP ${response.status}`;
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function deleteDocument(id: string) {
  return request<{ success: boolean }>(`/documents/${id}`, { method: 'DELETE' });
}

export async function getDocumentStats() {
  return request<{ data: any }>('/documents/stats/summary');
}

// Notifications
export async function getNotificationTypes() {
  return request<{ data: any[] }>('/notifications/types');
}

export async function getNotificationPreferences(userId?: string) {
  const query = userId ? `?userId=${userId}` : '';
  return request<{ data: any[] }>(`/notifications/preferences${query}`);
}

export async function updateNotificationPreferences(preferences: any[], userId?: string) {
  return request<{ success: boolean }>('/notifications/preferences', {
    method: 'PUT',
    body: JSON.stringify({ preferences, userId }),
  });
}

export async function getWebhooks() {
  return request<{ data: any[] }>('/notifications/webhooks');
}

export async function createWebhook(data: { name: string; url: string; secret?: string; events: string[] }) {
  return request<{ data: any }>('/notifications/webhooks', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateWebhook(id: string, data: any) {
  return request<{ data: any }>(`/notifications/webhooks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteWebhook(id: string) {
  return request<{ success: boolean }>(`/notifications/webhooks/${id}`, { method: 'DELETE' });
}

export async function testWebhook(id: string) {
  return request<{ success: boolean; status?: number; message: string }>(`/notifications/webhooks/${id}/test`, {
    method: 'POST',
  });
}

// API Keys
export async function getApiKeys(params?: { isActive?: boolean; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.isActive !== undefined) query.set('isActive', params.isActive.toString());
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/api-keys${queryString ? `?${queryString}` : ''}`);
}

export async function createApiKey(data: { name: string; description?: string; permissions?: string[]; expiresAt?: string; rateLimit?: number }) {
  return request<{ id: string; key: string; keyPrefix: string }>('/api-keys', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function revokeApiKey(id: string) {
  return request<{ success: boolean }>(`/api-keys/${id}`, { method: 'DELETE' });
}

export async function getApiKeyUsage(id: string) {
  return request<{ data: any[] }>(`/api-keys/${id}/usage`);
}

export async function getNotificationHistory(userId?: string, limit?: number) {
  const params = new URLSearchParams();
  if (userId) params.set('userId', userId);
  if (limit) params.set('limit', limit.toString());
  const query = params.toString();
  return request<{ data: any[] }>(`/notifications/history${query ? `?${query}` : ''}`);
}

export async function markNotificationRead(id: string) {
  return request<{ success: boolean }>(`/notifications/history/${id}/read`, { method: 'PUT' });
}

export async function markAllNotificationsRead(userId?: string) {
  const query = userId ? `?userId=${userId}` : '';
  return request<{ success: boolean }>(`/notifications/history/read-all${query}`, { method: 'PUT' });
}

export async function getUnreadNotificationCount(userId?: string) {
  const query = userId ? `?userId=${userId}` : '';
  return request<{ count: number }>(`/notifications/unread-count${query}`);
}

// Yield Analysis
export async function getYieldTargets() {
  return request<{ data: any[] }>('/yield-analysis/targets');
}

export async function setYieldTarget(data: {
  formulaId: string;
  formulaName: string;
  expectedYield: number;
  toleranceLower?: number;
  toleranceUpper?: number;
  notes?: string;
}) {
  return request<{ data: any }>('/yield-analysis/targets', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getYieldRecords(params?: { startDate?: string; endDate?: string; formulaId?: string; batchId?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  if (params?.formulaId) searchParams.set('formulaId', params.formulaId);
  if (params?.batchId) searchParams.set('batchId', params.batchId);
  const query = searchParams.toString();
  return request<{ data: any[] }>(`/yield-analysis/records${query ? `?${query}` : ''}`);
}

export async function recordYield(data: {
  batchId: string;
  batchNumber: string;
  productionOrderId?: string;
  formulaId: string;
  formulaName: string;
  expectedQuantity: number;
  actualQuantity: number;
  unit?: string;
  lossReasons?: string[];
  notes?: string;
}) {
  return request<{ data: any }>('/yield-analysis/records', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getYieldStats(startDate?: string, endDate?: string) {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  const query = params.toString();
  return request<{ data: any; period: any }>(`/yield-analysis/stats${query ? `?${query}` : ''}`);
}

export async function getYieldPerformance(startDate?: string, endDate?: string) {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  const query = params.toString();
  return request<{ data: any; period: any }>(`/yield-analysis/performance${query ? `?${query}` : ''}`);
}

export async function getLossReasons() {
  return request<{ data: any[] }>('/yield-analysis/loss-reasons');
}

// Batch Costing
export async function getCostCategories() {
  return request<{ data: any[] }>('/batch-costing/categories');
}

export async function getCostRates() {
  return request<{ data: any[] }>('/batch-costing/rates');
}

export async function createCostRate(data: { category: string; name: string; rate: number; unit?: string; notes?: string }) {
  return request<{ data: any }>('/batch-costing/rates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getBatchCosts(params?: { startDate?: string; endDate?: string; formulaId?: string; batchId?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  if (params?.formulaId) searchParams.set('formulaId', params.formulaId);
  if (params?.batchId) searchParams.set('batchId', params.batchId);
  const query = searchParams.toString();
  return request<{ data: any[] }>(`/batch-costing/batches${query ? `?${query}` : ''}`);
}

export async function recordBatchCost(data: {
  batchId: string;
  batchNumber: string;
  productionOrderId?: string;
  formulaId: string;
  formulaName: string;
  quantity: number;
  unit?: string;
  items: any[];
  notes?: string;
}) {
  return request<{ data: any }>('/batch-costing/batches', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getBatchCostStats(startDate?: string, endDate?: string) {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  const query = params.toString();
  return request<{ data: any; period: any }>(`/batch-costing/stats${query ? `?${query}` : ''}`);
}

export async function getBatchCostVariance(startDate?: string, endDate?: string) {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  const query = params.toString();
  return request<{ data: any[] }>(`/batch-costing/variance${query ? `?${query}` : ''}`);
}

// ============================================
// Phase 4: Advanced Quality Management
// ============================================

// QC Test Definitions
export async function getQcTestDefinitions(params?: { category?: string; activeOnly?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.category) searchParams.set('category', params.category);
  if (params?.activeOnly !== undefined) searchParams.set('activeOnly', params.activeOnly.toString());
  const query = searchParams.toString();
  return request<{ data: any[] }>(`/quality/test-definitions${query ? `?${query}` : ''}`);
}

export async function getQcTestDefinition(id: string) {
  return request<{ data: any }>(`/quality/test-definitions/${id}`);
}

export async function createQcTestDefinition(data: {
  code: string;
  name: string;
  category: string;
  description?: string;
  method?: string;
  unit?: string;
  minValue?: number;
  maxValue?: number;
  targetValue?: number;
  dataType?: string;
  isRequired?: boolean;
  appliesToLots?: boolean;
  appliesToBatches?: boolean;
  sortOrder?: number;
}) {
  return request<{ data: { id: string } }>('/quality/test-definitions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateQcTestDefinition(id: string, data: any) {
  return request<{ success: boolean }>(`/quality/test-definitions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// QC Test Results
export async function getLotTestResults(lotId: string) {
  return request<{ data: any[] }>(`/quality/lots/${lotId}/test-results`);
}

export async function getBatchTestResults(batchId: string) {
  return request<{ data: any[] }>(`/quality/batches/${batchId}/test-results`);
}

export async function recordTestResult(data: {
  testDefinitionId: string;
  lotId?: string;
  batchId?: string;
  numericValue?: number;
  textValue?: string;
  booleanValue?: boolean;
  status?: string;
  notes?: string;
}) {
  return request<{ data: { id: string; status: string } }>('/quality/test-results', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function approveTestResult(id: string) {
  return request<{ success: boolean }>(`/quality/test-results/${id}/approve`, {
    method: 'POST',
  });
}

// QC Specifications
export async function getQcSpecifications(formulaId: string) {
  return request<{ data: any[] }>(`/quality/specifications/formula/${formulaId}`);
}

export async function createQcSpecification(data: {
  formulaId: string;
  testDefinitionId: string;
  minValue?: number;
  maxValue?: number;
  targetValue?: number;
  isRequired?: boolean;
  sortOrder?: number;
  notes?: string;
}) {
  return request<{ data: { id: string } }>('/quality/specifications', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateQcSpecification(id: string, data: any) {
  return request<{ success: boolean }>(`/quality/specifications/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteQcSpecification(id: string) {
  return request<{ success: boolean }>(`/quality/specifications/${id}`, {
    method: 'DELETE',
  });
}

// Certificates (COA)
export async function getCertificates(params?: {
  type?: string;
  status?: string;
  batchId?: string;
  customerId?: string;
  page?: number;
  pageSize?: number;
}) {
  const searchParams = new URLSearchParams();
  if (params?.type) searchParams.set('type', params.type);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.batchId) searchParams.set('batchId', params.batchId);
  if (params?.customerId) searchParams.set('customerId', params.customerId);
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  const query = searchParams.toString();
  return request<PaginatedResponse<any>>(`/quality/certificates${query ? `?${query}` : ''}`);
}

export async function getCertificate(id: string) {
  return request<{ data: any }>(`/quality/certificates/${id}`);
}

export async function generateCoa(batchId: string, data?: { customerId?: string; notes?: string }) {
  return request<{ data: { id: string; certificateNumber: string } }>(
    `/quality/certificates/generate-coa/${batchId}`,
    { method: 'POST', body: JSON.stringify(data || {}) }
  );
}

export async function approveCertificate(id: string) {
  return request<{ success: boolean }>(`/quality/certificates/${id}/approve`, {
    method: 'POST',
  });
}

export async function issueCertificate(id: string, expiryDate?: string) {
  return request<{ success: boolean }>(`/quality/certificates/${id}/issue`, {
    method: 'POST',
    body: JSON.stringify({ expiryDate }),
  });
}

// QC Deviations (legacy /quality/deviations endpoints)
export async function getQcDeviations(params?: {
  status?: string;
  severity?: string;
  type?: string;
  assignedTo?: string;
  page?: number;
  pageSize?: number;
}) {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.severity) searchParams.set('severity', params.severity);
  if (params?.type) searchParams.set('type', params.type);
  if (params?.assignedTo) searchParams.set('assignedTo', params.assignedTo);
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  const query = searchParams.toString();
  return request<PaginatedResponse<any>>(`/quality/deviations${query ? `?${query}` : ''}`);
}

export async function getQcDeviation(id: string) {
  return request<{ data: any }>(`/quality/deviations/${id}`);
}

export async function createQcDeviation(data: {
  type: string;
  severity?: string;
  lotId?: string;
  batchId?: string;
  testResultId?: string;
  title: string;
  description: string;
  dueDate?: string;
  assignedTo?: string;
}) {
  return request<{ data: { id: string; deviationNumber: string } }>('/quality/deviations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateQcDeviation(id: string, data: any) {
  return request<{ success: boolean }>(`/quality/deviations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// QC Holds
export async function getQcHolds(status?: string) {
  const query = status ? `?status=${status}` : '';
  return request<{ data: any[] }>(`/quality/holds${query}`);
}

export async function createQcHold(data: {
  lotId?: string;
  batchId?: string;
  reason: string;
  notes?: string;
  deviationId?: string;
}) {
  return request<{ data: { id: string } }>('/quality/holds', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function releaseQcHold(id: string, notes?: string) {
  return request<{ success: boolean }>(`/quality/holds/${id}/release`, {
    method: 'POST',
    body: JSON.stringify({ notes }),
  });
}

export async function disposeQcHold(id: string, notes?: string) {
  return request<{ success: boolean }>(`/quality/holds/${id}/dispose`, {
    method: 'POST',
    body: JSON.stringify({ notes }),
  });
}

// QC Dashboard
export async function getQcDashboard() {
  return request<{ data: any }>('/quality/dashboard');
}

// Sample Management
export async function getSampleStatuses() {
  return request<{ data: any[] }>('/samples/statuses');
}

export async function getSampleTypes() {
  return request<{ data: any[] }>('/samples/types');
}

export async function getSampleRequests(params?: {
  status?: string;
  customerId?: string;
  type?: string;
  startDate?: string;
  endDate?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.customerId) searchParams.set('customerId', params.customerId);
  if (params?.type) searchParams.set('type', params.type);
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  const query = searchParams.toString();
  return request<{ data: any[]; total: number }>(`/samples/requests${query ? `?${query}` : ''}`);
}

export async function getSampleRequest(id: string) {
  return request<{ data: any }>(`/samples/requests/${id}`);
}

export async function createSampleRequest(data: {
  customerId: string;
  customerName: string;
  contactName?: string;
  contactEmail?: string;
  type?: string;
  purpose?: string;
  requestedDate?: string;
  requiredBy?: string;
  shippingAddress?: string;
  shippingMethod?: string;
  priority?: string;
  items: Array<{
    formulaId: string;
    formulaCode: string;
    formulaName: string;
    quantity: number;
    unit?: string;
    packaging?: string;
    concentration?: string;
    notes?: string;
  }>;
  internalNotes?: string;
  requestedBy?: string;
}) {
  return request<{ data: any }>('/samples/requests', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSampleRequest(id: string, data: any) {
  return request<{ data: any }>(`/samples/requests/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function updateSampleRequestStatus(id: string, data: {
  status: string;
  approvedBy?: string;
  trackingNumber?: string;
  carrier?: string;
}) {
  return request<{ data: any }>(`/samples/requests/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteSampleRequest(id: string) {
  return request<{ success: boolean }>(`/samples/requests/${id}`, { method: 'DELETE' });
}

export async function recordSampleFeedback(id: string, data: {
  rating?: number;
  overallImpression?: string;
  topNotes?: string;
  heartNotes?: string;
  baseNotes?: string;
  longevity?: string;
  sillage?: string;
  suggestions?: string;
  willProceed?: boolean;
  proceedQuantity?: number;
  comments?: string;
  respondedBy?: string;
}) {
  return request<{ data: any }>(`/samples/requests/${id}/feedback`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getSampleStats(startDate?: string, endDate?: string) {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  const query = params.toString();
  return request<{ data: any; period: any }>(`/samples/stats${query ? `?${query}` : ''}`);
}
